import * as signalR from "@microsoft/signalr";
import { createSignal } from "../../utils/signalDispatcher";
import http from "../api/http";

export interface IBulkEnrollmentMessageBody {
  EnrollmentIds: string[];
  Status: number;
  EnrollmentCustomerMap?: { [enrollmentId: string]: string };
}

type SignalRConnectionInfo = {
  url: string;
  accessToken: string;
};

// Delay before retrying the initial negotiate + start sequence when it fails at startup.
// Post-connect reconnects are handled automatically by withAutomaticReconnect().
const INITIAL_RETRY_DELAY_MS = 5000;

let connection: signalR.HubConnection | null = null;
let negotiateEndpoint = "";
let retryTimer: ReturnType<typeof setTimeout> | null = null;

export const EnrollmentUpdated = createSignal<IBulkEnrollmentMessageBody>();
export const Connected = createSignal<signalR.HubConnection>();
export const Disconnected = createSignal<signalR.HubConnection>();

export async function configure(phoenixApiUrl: string): Promise<void> {
  negotiateEndpoint = `${phoenixApiUrl}signalr/negotiateenrollment`;
}

async function establishConnection(): Promise<signalR.HubConnection | null> {
  let info: SignalRConnectionInfo;
  try {
    const { data } = await http.post<SignalRConnectionInfo>(negotiateEndpoint);
    info = data;
  } catch (error) {
    console.error('[EnrollmentHub] Failed to negotiate SignalR connection:', error);
    return null;
  }

  const options = {
    accessTokenFactory: () => info.accessToken,
  };

  return new signalR.HubConnectionBuilder()
    .withUrl(info.url, options)
    // Keep retrying with exponential backoff (1s, 2s, 4s, … max 30s).
    // Returning a number (never null) means retries continue indefinitely.
    // onclose is therefore only triggered by an explicit stop() call.
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext) =>
        Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000),
    })
    .configureLogging(signalR.LogLevel.Information)
    .build();
}

function bindEvents(conn: signalR.HubConnection): void {
  // Listen for enrollment status updates
  // Event name format: {messageTypeName}_{actionName}
conn.on('enrollment_BulkEnrollmentsUpdatedAsync', (payload: IBulkEnrollmentMessageBody) => {
  EnrollmentUpdated.dispatch(payload);
});

  conn.onreconnecting(() => {
    if (connection) Disconnected.dispatch(connection);
  });

  conn.onreconnected(() => {
    // No need to re-join server-side groups here: Azure SignalR keys group membership
    // by the userId claim from the negotiate-time access token, not by the ephemeral
    // connection ID, so membership survives a reconnect automatically.
    // If NegotiateEnrollment ever switches to connection-ID-scoped groups, this will
    // silently break — add re-confirmation logic (and an integration test) at that point.
    if (connection) Connected.dispatch(connection);
  });

  conn.onclose((error?: Error) => {
    // Only fires when stop() is called explicitly; automatic reconnect attempts
    // do not trigger onclose (they use onreconnecting / onreconnected instead).
    if (error) console.error('[EnrollmentHub] SignalR connection closed:', error);
    if (connection) Disconnected.dispatch(connection);
  });
}

export async function start(): Promise<void> {
  const conn = await establishConnection();
  if (!conn) {
    retryTimer = setTimeout(start, INITIAL_RETRY_DELAY_MS);
    return;
  }

  connection = conn;
  bindEvents(conn);

  try {
    await conn.start();
    console.log('[EnrollmentHub] SignalR connected.');
    Connected.dispatch(conn);
  } catch (e) {
    console.error('[EnrollmentHub] SignalR connection failed:', e);
    connection = null;
    retryTimer = setTimeout(start, INITIAL_RETRY_DELAY_MS);
  }
}

export function stop(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  if (connection) {
    connection.stop();
    connection = null;
  }
}
